import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { type Core, type WebViewerInstance } from '@pdftron/webviewer'
import { toast } from 'sonner'
import { hideAnnotationTooltips } from '~/utilities/webviewer/webviewer'
import { scalesDropdown } from '~/utilities/webviewer/scalesDropdown'
import { type Match } from '~/types/smartMatch'

type ViewerType = 'viewer1' | 'viewer2'

const UNMATCHED_PAGE_INDEX = -1

function isPolygonAnnotation(
  annotation: Core.Annotations.Annotation
): annotation is Core.Annotations.PolygonAnnotation {
  return annotation.Subject === 'Polygon'
}

function annotationPathToPolygon(
  polygonAnnotation: Core.Annotations.PolygonAnnotation
): [number, number][] {
  const path = polygonAnnotation.getPath()
  const polygon: [number, number][] = []
  path.forEach((_, index) => {
    const pathPoint = polygonAnnotation.getPathPoint(index) as Core.Math.Point
    polygon.push([pathPoint.getX(), pathPoint.getY()])
  })
  return polygon
}

function polygonsEqual(a: [number, number][], b: [number, number][]): boolean {
  if (a.length !== b.length) return false
  return a.every((point, index) => point[0] === b[index][0] && point[1] === b[index][1])
}

interface UseSmartMatchAnnotationsParams {
  smartMatchRef: MutableRefObject<Match[]>
  setSmartMatch: Dispatch<SetStateAction<Match[]>>
  lastViewerRef: MutableRefObject<ViewerType | null>
}

export function useSmartMatchAnnotations({
  smartMatchRef,
  setSmartMatch,
  lastViewerRef
}: UseSmartMatchAnnotationsParams) {
  const handleAnnotationAdded = useCallback(
    (
      viewer: Core.DocumentViewer,
      viewerType: ViewerType,
      annotations: Core.Annotations.Annotation[]
    ) => {
      const polygonAnnotations = annotations.filter(isPolygonAnnotation)
      if (polygonAnnotations.length === 0) return

      if (lastViewerRef.current === viewerType) {
        const annotationManager = viewer.getAnnotationManager()
        polygonAnnotations.forEach((annotation) => {
          annotationManager.deleteAnnotation(annotation)
        })
        toast.error('You must match the just added polygon with the other document.')
        return
      }

      try {
        const drawnPolygon = polygonAnnotations.at(-1)
        if (!drawnPolygon || !isPolygonAnnotation(drawnPolygon)) return

        viewer.getAnnotationManager().redrawAnnotation(drawnPolygon)

        const polygon = annotationPathToPolygon(drawnPolygon)
        const pageIndex = viewer.getCurrentPage() - 1

        const missingMatch = smartMatchRef.current.findIndex(
          (match) =>
            match.originalPageIndex === UNMATCHED_PAGE_INDEX ||
            match.revisedPageIndex === UNMATCHED_PAGE_INDEX
        )

        if (missingMatch > -1) {
          setSmartMatch((prev) => {
            const next = structuredClone(prev)
            if (viewerType === 'viewer1') {
              next[missingMatch].originalPageIndex = pageIndex
              next[missingMatch].originalPolygons = [polygon]
              next[missingMatch].orgPolygonId = drawnPolygon.Id
            } else {
              next[missingMatch].revisedPageIndex = pageIndex
              next[missingMatch].revisedPolygons = [polygon]
              next[missingMatch].revPolygonId = drawnPolygon.Id
            }
            return next
          })
          return
        }

        if (viewerType === 'viewer1') {
          setSmartMatch((prev) => [
            ...prev,
            {
              orgPolygonId: drawnPolygon.Id,
              revPolygonId: '',
              originalPageIndex: pageIndex,
              originalPolygons: [polygon],
              revisedPageIndex: UNMATCHED_PAGE_INDEX,
              revisedPolygons: [],
              polygon_scale: [1, 1]
            }
          ])
        }

        if (viewerType === 'viewer2') {
          setSmartMatch((prev) => [
            ...prev,
            {
              orgPolygonId: '',
              revPolygonId: drawnPolygon.Id,
              originalPageIndex: UNMATCHED_PAGE_INDEX,
              originalPolygons: [],
              revisedPageIndex: pageIndex,
              revisedPolygons: [polygon],
              polygon_scale: [1, 1]
            }
          ])
        }
      } catch {
        toast.error('Error processing polygon')
      } finally {
        lastViewerRef.current = viewerType
      }
    },
    [smartMatchRef, setSmartMatch, lastViewerRef]
  )

  const handleAnnotationDeleted = useCallback(
    (
      viewers: Core.DocumentViewer[],
      viewerType: ViewerType,
      annotations: Core.Annotations.Annotation[]
    ) => {
      if (annotations.length === 0) return

      const otherSideViewer = viewerType === 'viewer1' ? viewers[1] : viewers[0]
      const polygonRemovedFrom =
        viewerType === 'viewer1' ? 'originalPolygons' : 'revisedPolygons'

      const lastAnnotation = annotations.at(-1)
      if (!lastAnnotation || !isPolygonAnnotation(lastAnnotation)) return

      const polygon = annotationPathToPolygon(lastAnnotation)

      const removingMatchIndex = smartMatchRef.current.findIndex((match) => {
        const [originalPolygon] = match[polygonRemovedFrom]
        return originalPolygon && polygonsEqual(originalPolygon, polygon)
      })

      if (removingMatchIndex === -1) return

      const otherSideAnnotations = otherSideViewer.getAnnotationManager().getAnnotationsList()
      const otherSidePolygonAnnotations = otherSideAnnotations.filter(isPolygonAnnotation)

      otherSidePolygonAnnotations.forEach((otherSidePolygonAnnotation) => {
        const otherPolygon = annotationPathToPolygon(otherSidePolygonAnnotation)

        const otherSideMatchedDocument =
          polygonRemovedFrom === 'originalPolygons' ? 'revisedPolygons' : 'originalPolygons'
        const [otherSideMatchedDocumentPolygon] =
          smartMatchRef.current[removingMatchIndex][otherSideMatchedDocument]

        const isOtherSideToRemove =
          otherSideMatchedDocumentPolygon &&
          polygonsEqual(otherPolygon, otherSideMatchedDocumentPolygon)

        if (isOtherSideToRemove) {
          otherSideViewer.getAnnotationManager().deleteAnnotation(otherSidePolygonAnnotation)

          setSmartMatch((prev) => prev.filter((_, index) => index !== removingMatchIndex))
        }
      })
    },
    [smartMatchRef, setSmartMatch]
  )

  const handleAnnotationEdited = useCallback(
    (viewerType: ViewerType, annotations: Core.Annotations.Annotation[]) => {
      const polygonAnnotations = annotations.filter(isPolygonAnnotation)
      if (polygonAnnotations.length === 0) return

      const polygonIdKey = viewerType === 'viewer1' ? 'orgPolygonId' : 'revPolygonId'
      const polygonKey = viewerType === 'viewer1' ? 'originalPolygons' : 'revisedPolygons'

      polygonAnnotations.forEach((annotation) => {
        const polygon = annotationPathToPolygon(annotation)

        setSmartMatch((prev) => {
          const next = structuredClone(prev)
          const matchIndex = next.findIndex((match) => match[polygonIdKey] === annotation.Id)
          if (matchIndex === -1) return prev
          next[matchIndex][polygonKey] = [polygon]
          return next
        })
      })
    },
    [setSmartMatch]
  )

  const makeAnnotationSelectedHandler = useCallback(
    (
      webviewerInstance: WebViewerInstance,
      polygonIdKey: 'orgPolygonId' | 'revPolygonId',
      scaleIndex: 0 | 1
    ) =>
      (annotations: Core.Annotations.Annotation[]) => {
        hideAnnotationTooltips(webviewerInstance)

        const polygonAnnotations = annotations.filter(isPolygonAnnotation)
        if (polygonAnnotations.length === 0) return
        const polygon = polygonAnnotations.at(-1)
        if (!polygon) return

        const currentMatch = smartMatchRef.current.find(
          (match) => match[polygonIdKey] === polygon.Id
        )
        const currentScale = currentMatch?.polygon_scale[scaleIndex]

        const onScaleChange = (scale: number) => {
          setSmartMatch((prev) => {
            const next = structuredClone(prev)
            const matchIndex = next.findIndex((match) => match[polygonIdKey] === polygon.Id)
            if (matchIndex === -1) return prev
            const newScale: [number, number] = [...next[matchIndex].polygon_scale]
            newScale[scaleIndex] = scale
            next[matchIndex].polygon_scale = newScale
            return next
          })
        }

        const { UI } = webviewerInstance
        const iframeDoc = UI.iframeWindow.document
        const scalesDropdownElement = scalesDropdown(onScaleChange, iframeDoc, currentScale)

        const injectDropdown = () => {
          const popupContainer = iframeDoc.querySelector(
            '[data-element="annotationPopup"] .container'
          )
          if (popupContainer) {
            iframeDoc.querySelector('.mbue-scales-dropdown')?.remove()
            popupContainer.prepend(scalesDropdownElement)
            return true
          }
          return false
        }

        if (!injectDropdown()) {
          const observer = new MutationObserver((_, obs) => {
            if (injectDropdown()) obs.disconnect()
          })
          observer.observe(iframeDoc.body, { childList: true, subtree: true })
        }
      },
    [smartMatchRef, setSmartMatch]
  )

  return {
    handleAnnotationAdded,
    handleAnnotationDeleted,
    handleAnnotationEdited,
    makeAnnotationSelectedHandler
  }
}
